/** @jsx jsx */
import {ChangeEvent, Fragment, useEffect, useMemo, useState} from 'react'
import {jsx} from 'theme-ui'
import {
  BuildYourOwnWellItem,
  BuildYourOwnWellMetadata,
  BuildYourOwnWellPumpType,
  BuildYourOwnWellType,
  Metadata,
} from './shared'
import {useTranslation} from 'react-i18next'
import CustomSelect from './CustomSelect'

const buildYourOwnWellList: BuildYourOwnWellItem[] = [
  // {
  //   country: 'Chad',
  //   well_type: BuildYourOwnWellType.Deep,
  //   well_desc: 'Electric pump, Water tank and ablutions station',
  //   well_pump: BuildYourOwnWellPumpType.ElectricPumpAndWaterTank,
  //   unit_price: 1400,
  // },
  // {
  //   country: 'Ghana',
  //   well_type: BuildYourOwnWellType.Deep,
  //   well_desc: 'Deep well and Ablutions station',
  //   well_pump: BuildYourOwnWellPumpType.ElectricPumpAndWaterTank,
  //   unit_price: 2700,
  // },
  {
    country: 'Cameroun',
    well_type: BuildYourOwnWellType.Deep,
    well_desc: 'Deep well',
    well_pump: BuildYourOwnWellPumpType.Manual,
    unit_price: 1250,
  },
  // {
  //   country: 'Nepal',
  //   well_type: BuildYourOwnWellType.Surface,
  //   well_desc: 'Surface Well and Ablutions Station',
  //   well_pump: BuildYourOwnWellPumpType.Electric,
  //   unit_price: 480,
  // },
  // {
  //   country: 'Cambodia',
  //   well_type: BuildYourOwnWellType.Surface,
  //   well_desc: 'Surface Well',
  //   well_pump: BuildYourOwnWellPumpType.Electric,
  //   unit_price: 750,
  // },
  {
    country: 'Uganda',
    well_type: BuildYourOwnWellType.Surface,
    well_desc: 'Surface Well',
    well_pump: BuildYourOwnWellPumpType.Manual,
    unit_price: 680,
  },
  {
    country: 'Madagascar',
    well_type: BuildYourOwnWellType.Surface,
    well_desc: 'Surface Well',
    well_pump: BuildYourOwnWellPumpType.Manual,
    unit_price: 500,
  },
  {
    country: 'Cambodia',
    well_type: BuildYourOwnWellType.Surface,
    well_desc: 'Surface Well',
    well_pump: BuildYourOwnWellPumpType.ManualForOneFamily,
    unit_price: 200,
  },
  {
    country: 'Nepal',
    well_type: BuildYourOwnWellType.Surface,
    well_desc: 'Surface Well',
    well_pump: BuildYourOwnWellPumpType.ManualForOneFamily,
    unit_price: 160,
    disabled: true,
  },
  //   {
  //     country: 'Ghana',
  //     well_type: BuildYourOwnWellType.Surface,
  //     well_desc: 'Surface Well',
  //     well_pump: BuildYourOwnWellPumpType.Manual,
  //     unit_price: 900,
  //     disabled: true,
  //   },
  // {
  //   country: 'Sri Lanka',
  //   well_type: BuildYourOwnWellType.Surface,
  //   well_desc: 'Surface Well',
  //   well_pump: BuildYourOwnWellPumpType.Electric,
  //   unit_price: 620,
  //   disabled: true,
  // },
  // {
  //   country: 'Sri Lanka',
  //   well_type: BuildYourOwnWellType.Surface,
  //   well_desc: 'Surface Well',
  //   well_pump: BuildYourOwnWellPumpType.ManualForOneFamily,
  //   unit_price: 195,
  //   disabled: true,
  // },
]

interface IBuildYourOwnWell {
  option: number
  setOption: (option: number) => void
  onAmountChange: (
    amount: number | null,
    userInput: null | 'custom' | 'fixed',
  ) => void
  metadata: Metadata | null
  setMetadata: (metadata: BuildYourOwnWellMetadata | null) => void
}

const buildMetadata = (
  name_on_well: string,
  item: BuildYourOwnWellItem,
): BuildYourOwnWellMetadata => {
  return {
    type: 'Build your own well',
    quantity: 1,
    name_on_well,
    ...item,
  }
}

const BuildYourOwnWell: React.FC<IBuildYourOwnWell> = ({
  option,
  setOption,
  onAmountChange,
  metadata,
  setMetadata,
}) => {
  const {t} = useTranslation()
  const [name, setName] = useState<string>(
    metadata && 'name_on_well' in metadata ? metadata.name_on_well : '',
  )
  const [itemIndex, setItemIndex] = useState<number>(option)
  const selectBuildYourOwnWellList = useMemo(
    () => [
      {value: '-1', label: t('Select the type of well')},
      ...buildYourOwnWellList.map((item, index) => {
        const label = (
          <Fragment>
            {t(item.country)} - {t(item.well_desc)} -{' '}
            <strong>${item.unit_price} USD </strong>
          </Fragment>
        )
        return {
          value: `${index}`,
          selectedLabel: label,
          label: (
            <Fragment>
              {label}
              <div>{t(item.well_pump)}</div>
              {item.disabled && (
                <span
                  sx={{
                    fontSize: '10px',
                    bg: 'primary',
                    color: 'white',
                  }}
                >
                  {t('Temporarily Unavailable')}
                </span>
              )}
            </Fragment>
          ),
          isDisabled: item.disabled,
        }
      }),
    ],
    [t],
  )

  useEffect(() => {
    setOption(itemIndex)
    if (itemIndex !== -1) {
      setMetadata(buildMetadata(name, buildYourOwnWellList[itemIndex]))
      onAmountChange(buildYourOwnWellList[itemIndex].unit_price, 'fixed')
    } else {
      setMetadata(null)
      onAmountChange(null, null)
    }
  }, [setMetadata, name, itemIndex, onAmountChange, setOption])

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    setName(value)
  }

  const handleChangeType = (item: any) => {
    setItemIndex(parseInt(item.value))
  }

  return (
    <div
      sx={{
        py: 16,
        px: 13,
      }}
    >
      <CustomSelect
        value={selectBuildYourOwnWellList[itemIndex + 1]}
        onChange={handleChangeType}
        options={selectBuildYourOwnWellList}
      />
      <input
        type="text"
        sx={{
          mt: 14,
          width: '100%',
          height: '100%',
          border: '1px solid #DEE3E3',
          borderRadius: 4,
          padding: '15px 12px',
          fontSize: 13,
          outline: 0,
          '::placeholder': {
            color: '#717A7B',
          },
          '&:focus': {
            border: '1px solid #055064',
          },
        }}
        required
        placeholder={t('Name to be written on the well sign')}
        value={name}
        onChange={handleChangeName}
      />
      <div
        sx={{
          padding: '15px 0px 0px 4px',
        }}
      >
        {t('buildYourOwnWellDelayMessage')}
      </div>
    </div>
  )
}

export default BuildYourOwnWell
